import React, { createContext, useContext, useReducer, useEffect } from 'react'

const AssessmentContext = createContext()

const initialState = {
  currentAssessment: null,
  assessmentHistory: [],
  currentQuestionIndex: 0,
  answers: {},
  score: 0,
  riskLevel: 'unknown', // 'low', 'medium', 'high', 'unknown'
  isCompleted: false,
  lastAssessmentDate: null,
  recommendations: [],
  categories: {
    dataProtection: { score: 0, total: 0 },
    accessControl: { score: 0, total: 0 },
    networkSecurity: { score: 0, total: 0 },
    incidentResponse: { score: 0, total: 0 },
    training: { score: 0, total: 0 },
    compliance: { score: 0, total: 0 }
  }
}

function assessmentReducer(state, action) {
  switch (action.type) {
    case 'START_ASSESSMENT':
      return {
        ...state,
        currentAssessment: action.payload,
        currentQuestionIndex: 0,
        answers: {},
        score: 0,
        riskLevel: 'unknown',
        isCompleted: false,
        recommendations: []
      }
    case 'ANSWER_QUESTION':
      return {
        ...state,
        answers: {
          ...state.answers,
          [action.payload.questionId]: action.payload.answer
        }
      }
    case 'NEXT_QUESTION':
      return {
        ...state,
        currentQuestionIndex: state.currentQuestionIndex + 1
      }
    case 'PREVIOUS_QUESTION':
      return {
        ...state,
        currentQuestionIndex: Math.max(0, state.currentQuestionIndex - 1)
      }
    case 'COMPLETE_ASSESSMENT':
      const newAssessment = {
        id: Date.now(),
        date: new Date().toISOString(),
        score: action.payload.score,
        riskLevel: action.payload.riskLevel,
        answers: state.answers,
        categories: action.payload.categories,
        recommendations: action.payload.recommendations
      }
      return {
        ...state,
        score: action.payload.score,
        riskLevel: action.payload.riskLevel,
        isCompleted: true,
        lastAssessmentDate: new Date().toISOString(),
        recommendations: action.payload.recommendations,
        categories: action.payload.categories,
        assessmentHistory: [newAssessment, ...state.assessmentHistory]
      }
    case 'RESET_ASSESSMENT':
      return {
        ...state,
        currentAssessment: null,
        currentQuestionIndex: 0,
        answers: {},
        score: 0,
        riskLevel: 'unknown',
        isCompleted: false,
        recommendations: []
      }
    case 'LOAD_FROM_STORAGE':
      return {
        ...state,
        ...action.payload
      }
    default:
      return state
  }
}

export function AssessmentProvider({ children }) {
  const [state, dispatch] = useReducer(assessmentReducer, initialState)

  // Load assessment data from localStorage on mount
  useEffect(() => {
    const savedAssessmentData = localStorage.getItem('cybergov-assessments')
    if (savedAssessmentData) {
      try {
        const assessmentData = JSON.parse(savedAssessmentData)
        dispatch({ type: 'LOAD_FROM_STORAGE', payload: assessmentData })
      } catch (error) {
        console.error('Error loading assessment data from storage:', error)
      }
    }
  }, [])

  // Save assessment data to localStorage whenever state changes
  useEffect(() => {
    localStorage.setItem('cybergov-assessments', JSON.stringify(state))
  }, [state])

  const startAssessment = (assessmentType = 'general') => {
    dispatch({ type: 'START_ASSESSMENT', payload: { type: assessmentType, startTime: new Date() } })
  }

  const answerQuestion = (questionId, answer) => {
    dispatch({ type: 'ANSWER_QUESTION', payload: { questionId, answer } })
  }

  const nextQuestion = () => {
    dispatch({ type: 'NEXT_QUESTION' })
  }

  const previousQuestion = () => {
    dispatch({ type: 'PREVIOUS_QUESTION' })
  }

  const calculateScore = (answers, questions) => {
    let totalScore = 0
    let maxScore = questions.length
    const categories = {
      dataProtection: { score: 0, total: 0 },
      accessControl: { score: 0, total: 0 },
      networkSecurity: { score: 0, total: 0 },
      incidentResponse: { score: 0, total: 0 },
      training: { score: 0, total: 0 },
      compliance: { score: 0, total: 0 }
    }

    questions.forEach(question => {
      const answer = answers[question.id]
      const category = question.category || 'compliance'
      
      categories[category].total += 1
      
      if (answer === 'yes') {
        totalScore += 1
        categories[category].score += 1
      }
    })

    const percentage = (totalScore / maxScore) * 100
    let riskLevel = 'high'
    
    if (percentage >= 80) {
      riskLevel = 'low'
    } else if (percentage >= 60) {
      riskLevel = 'medium'
    }

    return { score: percentage, riskLevel, categories }
  }

  const generateRecommendations = (answers, questions, riskLevel) => {
    const recommendations = []
    
    questions.forEach(question => {
      const answer = answers[question.id]
      if (answer === 'no' && question.recommendation) {
        recommendations.push({
          id: question.id,
          category: question.category,
          priority: question.priority || 'medium',
          title: question.recommendation.title,
          description: question.recommendation.description,
          actions: question.recommendation.actions || []
        })
      }
    })

    // Sort by priority
    const priorityOrder = { 'high': 3, 'medium': 2, 'low': 1 }
    recommendations.sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority])

    return recommendations
  }

  const completeAssessment = (questions) => {
    const { score, riskLevel, categories } = calculateScore(state.answers, questions)
    const recommendations = generateRecommendations(state.answers, questions, riskLevel)
    
    dispatch({
      type: 'COMPLETE_ASSESSMENT',
      payload: { score, riskLevel, categories, recommendations }
    })
  }

  const resetAssessment = () => {
    dispatch({ type: 'RESET_ASSESSMENT' })
  }

  const getRiskLevelColor = (level = state.riskLevel) => {
    switch (level) {
      case 'low':
        return 'text-success-600 bg-success-50 border-success-200'
      case 'medium':
        return 'text-warning-600 bg-warning-50 border-warning-200'
      case 'high':
        return 'text-security-600 bg-security-50 border-security-200'
      default:
        return 'text-gov-600 bg-gov-50 border-gov-200'
    }
  }

  const getRiskLevelIcon = (level = state.riskLevel) => {
    switch (level) {
      case 'low':
        return '🟢'
      case 'medium':
        return '🟡'
      case 'high':
        return '🔴'
      default:
        return '⚪'
    }
  }

  const value = {
    ...state,
    startAssessment,
    answerQuestion,
    nextQuestion,
    previousQuestion,
    completeAssessment,
    resetAssessment,
    getRiskLevelColor,
    getRiskLevelIcon
  }

  return (
    <AssessmentContext.Provider value={value}>
      {children}
    </AssessmentContext.Provider>
  )
}

export function useAssessment() {
  const context = useContext(AssessmentContext)
  if (!context) {
    throw new Error('useAssessment must be used within an AssessmentProvider')
  }
  return context
}

export { AssessmentContext }
export default AssessmentContext